import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from 'react-oidc-context';
import PatientService from '../../services/patientService';
import { getTodayDateString } from '../../utils';
import './PatientModal.css';

const PatientModal = ({ 
  isOpen = false, 
  onClose = () => {}, 
  onPatientCreated = () => {},
  initialData = {},
  searchTerm = ''
}) => {
  const auth = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: initialData.firstName || '',
    lastName: initialData.lastName || '',
    phoneNumber: initialData.phoneNumber || '',
    email: initialData.email || '',
    dateOfBirth: initialData.dateOfBirth || ''
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !isLoading) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'El nombre es requerido';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'El nombre debe tener al menos 2 caracteres';
    } else if (formData.firstName.trim().length > 50) {
      newErrors.firstName = 'El nombre no puede exceder 50 caracteres';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'El apellido es requerido';
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'El apellido debe tener al menos 2 caracteres';
    } else if (formData.lastName.trim().length > 50) {
      newErrors.lastName = 'El apellido no puede exceder 50 caracteres';
    }

    if (formData.dateOfBirth && formData.dateOfBirth > getTodayDateString('UTC')) {
      newErrors.dateOfBirth = 'La fecha de nacimiento no puede ser futura';
    }
    
    // Email validation only if provided
    if (formData.email && formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El formato del email no es válido';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const token = auth.user?.access_token;
      
      // Clean data before sending
      const patientData = {
        ...formData,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        dateOfBirth: formData.dateOfBirth || null
      };
      
      const response = await PatientService.createPatient(patientData, token);
      
      // Call the callback with the newly created patient
      onPatientCreated(response);
      
      // Close the modal
      onClose();
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: '',
        dateOfBirth: ''
      });
      setErrors({});
    } catch (error) {
      console.error('Error creating patient:', error);
      setErrors({
        submit: error.message || 'Error al crear el paciente. Por favor, intenta nuevamente.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: '',
      dateOfBirth: ''
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="modal-overlay patient-modal-overlay">
      <div className="modal patient-modal">
        <div className="modal-header">
          <h3>Agregar Nuevo Paciente</h3>
          <button 
            className="modal-close" 
            onClick={handleClose}
            disabled={isLoading}
          >
            ✕
          </button>
        </div>
        
        <div className="modal-form">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="firstName">
                Nombre <span className="required">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Ingrese el nombre"
                className={errors.firstName ? 'error' : ''}
                disabled={isLoading}
              />
              {errors.firstName && (
                <span className="error-message">{errors.firstName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="lastName">
                Apellido <span className="required">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Ingrese el apellido"
                className={errors.lastName ? 'error' : ''}
                disabled={isLoading}
              />
              {errors.lastName && (
                <span className="error-message">{errors.lastName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="dateOfBirth">
                Fecha de Nacimiento
              </label>
              <input
                type="date"
                id="dateOfBirth"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                max={getTodayDateString('UTC')}
                className={errors.dateOfBirth ? 'error' : ''}
                disabled={isLoading}
              />
              {errors.dateOfBirth && (
                <span className="error-message">{errors.dateOfBirth}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="phoneNumber">
                Celular
              </label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Ej: +1234567890"
                className={errors.phoneNumber ? 'error' : ''}
                disabled={isLoading}
              />
              {errors.phoneNumber && (
                <span className="error-message">{errors.phoneNumber}</span>
              )}
            </div>

            <div className="form-group full-width">
              <label htmlFor="email">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="ejemplo@correo.com"
                className={errors.email ? 'error' : ''}
                disabled={isLoading}
              />
              {errors.email && (
                <span className="error-message">{errors.email}</span>
              )}
            </div>
          </div>

          {errors.submit && (
            <div className="error-message submit-error">
              {errors.submit}
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? 'Creando...' : 'Crear Paciente'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PatientModal;